import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDownIcon, CheckIcon } from 'lucide-react';
type Region = {
  code: string;
  // ISO 3166-1 alpha-2, lowercased, used for the flag image.
  iso: string;
  name: string;
  available: boolean;
};
// Talab launches in the UAE first; other GCC markets follow.
const REGIONS: Region[] = [{
  code: 'UAE',
  iso: 'ae',
  name: 'United Arab Emirates',
  available: true
}, {
  code: 'KSA',
  iso: 'sa',
  name: 'Saudi Arabia',
  available: false
}, {
  code: 'QAT',
  iso: 'qa',
  name: 'Qatar',
  available: false
}, {
  code: 'KWT',
  iso: 'kw',
  name: 'Kuwait',
  available: false
}, {
  code: 'BHR',
  iso: 'bh',
  name: 'Bahrain',
  available: false
}, {
  code: 'OMN',
  iso: 'om',
  name: 'Oman',
  available: false
}];
function Flag({
  iso,
  className = ''
}: {
  iso: string;
  className?: string;
}) {
  return <img src={`https://flagcdn.com/w40/${iso}.png`} srcSet={`https://flagcdn.com/w80/${iso}.png 2x`} width={20} height={15} alt="" aria-hidden="true" className={`rounded-[3px] object-cover shadow-sm ring-1 ring-black/5 ${className}`} data-id="element-2955" data-mp="2wri72upxqvn4" />;
}
export function RegionSelector({
  compact = false
}: {
  compact?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState<Region>(REGIONS[0]);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);
  const choose = (r: Region) => {
    if (!r.available) return;
    setActive(r);
    setOpen(false);
  };
  return <div className="relative" ref={ref} data-id="element-2956" data-mp="3thsluhbcnja4">
      <button type="button" onClick={() => setOpen(o => !o)} aria-haspopup="listbox" aria-expanded={open} aria-label={`Region: ${active.name}`} className={`flex items-center gap-2 rounded-full border transition-all ${compact ? 'px-2.5 py-1.5' : 'px-3 py-2'} ${open ? 'border-primary-indigo/40 bg-primary-indigo/5 shadow-sm' : 'border-slate-200 bg-white hover:bg-slate-50 hover:border-slate-300'}`} data-id="element-2957" data-mp="3tgpy2i9bpwyf">
        <Flag iso={active.iso} className="w-5 h-[15px]" data-id="element-2958" data-mp="18tr8xey93vcx" />
        {!compact && <span className="text-sm font-semibold text-slate-700 tracking-wide" data-id="element-2959" data-mp="1cvcfkik6zdeo">
            {active.code}
          </span>}
        <ChevronDownIcon className={`w-4 h-4 text-slate-400 transition-transform duration-200 ${open ? 'rotate-180 text-primary-indigo' : ''}`} data-id="element-2960" data-mp="yx4qewtedge2" />
      </button>

      <AnimatePresence data-id="element-2961" data-mp="2t9kt5nmzice6">
        {open && <motion.div role="listbox" initial={{
        opacity: 0,
        y: -8,
        scale: 0.97
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: -8,
        scale: 0.97
      }} transition={{
        duration: 0.16,
        ease: [0.16, 1, 0.3, 1]
      }} className="absolute right-0 mt-2.5 w-72 bg-white border border-slate-100 rounded-2xl shadow-[0_16px_48px_-8px_rgb(0,0,0,0.18)] overflow-hidden z-50" data-id="element-2962" data-mp="2tanme27s4qx9">
            <div className="px-4 pt-3.5 pb-2" data-id="element-2963" data-mp="10byqv2mikq0z">
              <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-slate-400" data-id="element-2964" data-mp="10fcu0d30539h">
                Your region
              </p>
            </div>
            <div className="px-2 pb-2 space-y-0.5" data-id="element-2965" data-mp="1ac7omkw5mjih">
              {REGIONS.map(r => {
            const isActive = r.code === active.code;
            return <button key={r.code} type="button" role="option" aria-selected={isActive} onClick={() => choose(r)} disabled={!r.available} className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-colors ${isActive ? 'bg-primary-indigo/[0.06]' : r.available ? 'hover:bg-slate-50' : 'cursor-not-allowed'}`} data-id="element-2966" data-mp="3c32nxmpkl0ty">
                    <Flag iso={r.iso} className={`w-7 h-[21px] ${!r.available ? 'opacity-50 saturate-[0.4]' : ''}`} data-id="element-2967" data-mp="1kfmsn7ksna3i" />
                    <span className="flex-1 min-w-0" data-id="element-2968" data-mp="1tft4yq4ra413">
                      <span className={`block text-sm truncate ${isActive ? 'font-semibold text-primary-indigo' : r.available ? 'font-medium text-slate-800' : 'font-medium text-slate-400'}`} data-id="element-2969" data-mp="1te8a0zej4j2p">
                        {r.name}
                      </span>
                      {!r.available && <span className="block text-[11px] text-slate-400 mt-0.5" data-id="element-2970" data-mp="y6plgn3beuvh">
                          Coming soon
                        </span>}
                    </span>
                    {isActive ? <span className="w-5 h-5 rounded-full bg-primary-indigo flex items-center justify-center shrink-0" data-id="element-2971" data-mp="p4yg5jcosq25">
                        <CheckIcon className="w-3 h-3 text-white" data-id="element-2972" data-mp="k6n3z1yx63gr" />
                      </span> : r.available ? <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-400" data-id="element-2973" data-mp="ah3lhxf4cbmf">
                        {r.code}
                      </span> : null}
                  </button>;
          })}
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}